import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCompany } from '../api';
import { coverageNote, day, money, num, stateLabel, stateNote, words } from '../format';
import DetailSkeleton from './DetailSkeleton';
import Flag from './Flag';
import Logo from './Logo';

/* This page has to be worth reading in a week when nothing is moving, which is
 * most weeks for most companies. So it leads with what we cover and why - the
 * brands, the revenue behind them, the terms and the markets we chose - and
 * treats episodes as history rather than as the only content. */

export default function CompanyView() {
  const companyId = Number(useParams().companyId);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    getCompany(companyId).then(setData).catch(() => setError('Could not load this company'));
  }, [companyId]);

  if (error) return <div className="tw-empty">{error}</div>;
  if (!data) return <DetailSkeleton />;

  const { company: c, brands, series, competitors, episodes } = data;
  /* Tier 2 - the 'category' and 'upstream_need' terms the extraction produced -
   * is not shown. None of the 14,821 of them has ever been polled, so the table
   * was a list of names with no curve, no status and nothing to click. */
  const scanned = series.filter((s) => s.monitor_tier === 1);
  const linked = competitors.filter((x) => x.universe_company_id);
  const open = episodes.filter((e) => e.is_open);

  return (
    <div className="tw-detail">
      <Link className="tw-back" to="/companies">&larr; All companies</Link>

      <div className="tw-detail-head">
        <h1 className="tw-h1">
          <Logo symbol={c.eodhd_symbol} name={c.name} ticker={c.ticker} size={34} />
          <span className="sym">{c.ticker}</span>
          {c.name}
        </h1>
        <div className="tw-crumbs">
          {[c.exchange, c.country, c.sector, c.industry].filter(Boolean).map((bit, i) => (
            <React.Fragment key={bit}>
              {i > 0 && <span className="sep">/</span>}
              {bit}
            </React.Fragment>
          ))}
        </div>
        {/* A sentence describing what the company sells is a subtitle, not a
            label. It went in a pill first and read like a very long tag. */}
        {c.category_name && <div className="tw-subtitle">{c.category_name}</div>}
        {open.length > 0 && (
          <div className="tw-tags">
            <span className="tw-tag good">
              {open.length} trend{open.length === 1 ? '' : 's'} open
            </span>
          </div>
        )}
      </div>

      <div className="tw-block">
        <div className="tw-kv">
          <div>
            <div className="v">{money(c.market_cap_usd)}</div>
            <div className="k">Market cap</div>
          </div>
          <div>
            <div className="v">{scanned.length}</div>
            <div className="k">Terms scanned weekly</div>
          </div>
          <div>
            <div className="v">{brands.length}</div>
            <div className="k">Brands covered</div>
          </div>
          {/* "Demand to revenue lag" used to sit here. It was a single number
            * the model guessed for the whole company, it was never measured
            * against anything, and nothing else on the site used it. */}
          <div>
            <div className="v">{episodes.length}</div>
            <div className="k">Trends on record</div>
          </div>
        </div>
      </div>

      {brands.length > 0 && (
        <div className="tw-block">
          <h2 className="tw-block-title">Brands</h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th colSpan={2}>Share of revenue</th>
                  <th>Basis</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.brand_id}>
                    <td className="lead" style={{ width: 200 }}>{b.brand}</td>
                    <td className="tw-bar-cell" style={{ width: '42%' }}>
                      {b.revenue_share_pct ? (
                        <div className="tw-bar-track">
                          <div
                            className="tw-bar-fill"
                            style={{ width: `${Math.max(2, Math.min(100, b.revenue_share_pct))}%` }}
                          />
                        </div>
                      ) : null}
                    </td>
                    <td className="n lead" style={{ width: 64 }}>
                      {b.revenue_share_pct ? `${num(b.revenue_share_pct)}%` : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {words(b.revenue_share_basis)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {episodes.length > 0 && (
        <div className="tw-block">
          <h2 className="tw-block-title">Trends</h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Term</th>
                  <th>Market</th>
                  <th>State</th>
                  <th className="n">Weeks</th>
                  <th>Ended</th>
                </tr>
              </thead>
              <tbody>
                {episodes.map((e) => (
                  <tr
                    key={e.episode_id}
                    className="clickable"
                    onClick={() => navigate(`/trends/${e.episode_id}`)}
                  >
                    <td className="lead">{day(e.started_on)}</td>
                    <td>{e.term}</td>
                    <td><Flag geo={e.geo} /></td>
                    <td title={stateNote(e.state) || undefined}>{stateLabel(e.state)}</td>
                    <td className="n">{e.weeks_open ?? '—'}</td>
                    <td>{e.is_open ? 'Open' : words(e.close_reason) || 'closed'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="tw-block">
        <h2 className="tw-block-title">Terms scanned weekly ({scanned.length})</h2>
        <div className="tw-table-wrap">
          <table className="tw-table">
            <thead>
              <tr>
                <th>Term</th>
                <th>Market</th>
                <th>Role</th>
                <th className="n">Proxy</th>
                <th className="n">Latest</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {scanned.map((s) => (
                <tr key={s.series_id} className="clickable"
                  onClick={() => navigate(`/terms/${s.series_id}`)}>
                  <td className="lead">{s.term}</td>
                  <td><Flag geo={s.geo} /></td>
                  <td>{words(s.term_role)}</td>
                  <td className="n">{s.demand_proxy_quality ?? '—'}</td>
                  <td className="n">{num(s.latest_value)}</td>
                  <td title={coverageNote(s) || ''}>
                    {s.episode_id ? (
                      <span style={{ color: 'var(--up)' }}>{stateLabel(s.episode_state)}</span>
                    ) : (
                      words(s.status) || 'not yet scanned'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {competitors.length > 0 && (
        <div className="tw-block">
          <h2 className="tw-block-title">
            Competitors — {linked.length} of {competitors.length} also covered
          </h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Competitor</th>
                  <th>Covered</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((x) => (
                  <tr
                    key={x.competitor_id}
                    className={x.universe_company_id ? 'clickable' : ''}
                    onClick={() =>
                      x.universe_company_id && navigate(`/companies/${x.universe_company_id}`)
                    }
                  >
                    <td className="lead" style={{ width: 200 }}>{x.brand}</td>
                    <td style={{ width: 230 }}>{x.competitor_name || ''}</td>
                    <td>
                      {x.universe_company_id ? (
                        <span style={{ color: 'var(--accent)' }}>{x.universe_ticker}</span>
                      ) : (
                        <span style={{ color: 'var(--fg-dim)' }}>
                          {x.listed ? 'Listed, not covered' : 'Private'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
